/**
 * printRouter.ts
 *
 * Capa de routing unificada que reemplaza los checks isSunmiCandidate() dispersos.
 * Cada función decide a qué destino enviar según la selección del usuario.
 * NO hay fallback silencioso entre destinos.
 */

import { isNativePlatform } from '../platform';
import { getSelectedDestination } from './printerSelection';
import { getSavedBlePrinter, printViaBle, printRawBytesViaBle } from './blePrinter';
import {
  sunmiPrintRaw,
  printTicketNativeDirect,
  reprintTicketNativeDirect,
  printListNativeDirect,
  listDataToText,
  type DirectPrintResult,
  type ListPrintData,
} from './nativeDirectPrint';
import { payloadToTicketText, ticketPayloadToEscPosBytes, resumenPremiosToText } from './escposPrinter';

// Re-export for consumers
export type { DirectPrintResult, ListPrintData };

// ---------------------------------------------------------------------------
// Core check: replaces isSunmiCandidate() everywhere
// ---------------------------------------------------------------------------

export function isNativePrintSelected(): boolean {
  if (!isNativePlatform()) return false;
  const dest = getSelectedDestination();
  return dest !== 'browser';
}

// ---------------------------------------------------------------------------
// Raw text print
// ---------------------------------------------------------------------------

export async function printRawByDestination(
  text: string,
  tag: string,
): Promise<DirectPrintResult> {
  const dest = getSelectedDestination();
  console.log(`[PRINT DEST ${tag}] destino=${dest}`);

  if (dest === 'sunmi') {
    return sunmiPrintRaw(text, tag);
  }

  if (dest === 'bluetooth-ble') {
    const printer = getSavedBlePrinter();
    if (!printer) {
      return { ok: false, error: 'No hay impresora Bluetooth configurada. Vaya a Configuracion > Impresora.' };
    }
    return printViaBle(text, printer);
  }

  // browser — caller handles web path
  return { ok: false, error: 'browser' };
}

// ---------------------------------------------------------------------------
// Ticket print (POS)
// ---------------------------------------------------------------------------

type TicketPayload = {
  ticketCode: string;
  sorteoNombre: string;
  fecha?: string;
  total: number;
  jugadas: Array<{ num1: string; num2?: string | null; monto: number; tipoSorteo?: string }>;
  localNombre?: string;
  localDescripcion?: string;
  sorteoCierre?: string;
  vendedorNombre?: string;
  clienteNombre?: string;
  pagaVecesAplicado?: string | null;
  esReimpresion?: boolean;
  leyendaLocal?: string;
  localTelefono?: string;
  estado?: string;
};

export async function printTicketByDestination(
  payload: TicketPayload,
): Promise<DirectPrintResult> {
  const dest = getSelectedDestination();
  console.log(`[PRINT DEST TICKET] destino=${dest}`);

  if (dest === 'sunmi') {
    return printTicketNativeDirect(payload);
  }

  if (dest === 'bluetooth-ble') {
    const printer = getSavedBlePrinter();
    if (!printer) {
      return { ok: false, error: 'No hay impresora Bluetooth configurada.' };
    }
    try {
      const bytes = ticketPayloadToEscPosBytes(payload);
      return await printRawBytesViaBle(bytes, printer);
    } catch (err: any) {
      console.warn('[PRINT DEST TICKET] ESC/POS bytes falló, fallback a texto:', err?.message);
      const text = payloadToTicketText(payload);
      return printViaBle(text, printer);
    }
  }

  return { ok: false, error: 'browser' };
}

// ---------------------------------------------------------------------------
// Reprint ticket
// ---------------------------------------------------------------------------

export async function reprintTicketByDestination(
  payload: TicketPayload,
): Promise<DirectPrintResult> {
  const dest = getSelectedDestination();
  console.log(`[PRINT DEST REPRINT] destino=${dest}`);

  if (dest === 'sunmi') {
    return reprintTicketNativeDirect(payload);
  }

  if (dest === 'bluetooth-ble') {
    const printer = getSavedBlePrinter();
    if (!printer) {
      return { ok: false, error: 'No hay impresora Bluetooth configurada.' };
    }
    try {
      const bytes = ticketPayloadToEscPosBytes({ ...payload, esReimpresion: true });
      return await printRawBytesViaBle(bytes, printer);
    } catch (err: any) {
      console.warn('[PRINT DEST REPRINT] ESC/POS bytes falló, fallback a texto:', err?.message);
      const text = payloadToTicketText({ ...payload, esReimpresion: true });
      return printViaBle(text, printer);
    }
  }

  return { ok: false, error: 'browser' };
}

// ---------------------------------------------------------------------------
// List print (consolidada)
// ---------------------------------------------------------------------------

export async function printListByDestination(
  data: ListPrintData,
): Promise<DirectPrintResult> {
  const dest = getSelectedDestination();
  console.log(`[PRINT DEST LIST] destino=${dest}`);

  if (dest === 'sunmi') {
    return printListNativeDirect(data);
  }

  if (dest === 'bluetooth-ble') {
    const printer = getSavedBlePrinter();
    if (!printer) {
      return { ok: false, error: 'No hay impresora Bluetooth configurada.' };
    }
    const text = listDataToText(data);
    return printViaBle(text, printer);
  }

  return { ok: false, error: 'browser' };
}

// ---------------------------------------------------------------------------
// Resumen premios (ganadores)
// ---------------------------------------------------------------------------

export async function printResumenByDestination(
  payload: Parameters<typeof resumenPremiosToText>[0],
): Promise<DirectPrintResult> {
  const dest = getSelectedDestination();
  console.log(`[PRINT DEST RESUMEN] destino=${dest}`);

  if (dest === 'sunmi') {
    const text = resumenPremiosToText(payload);
    return sunmiPrintRaw(text, 'RESUMEN PREMIOS');
  }

  if (dest === 'bluetooth-ble') {
    const printer = getSavedBlePrinter();
    if (!printer) {
      return { ok: false, error: 'No hay impresora Bluetooth configurada.' };
    }
    const text = resumenPremiosToText(payload);
    return printViaBle(text, printer);
  }

  return { ok: false, error: 'browser' };
}
